import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { appelsAPI, VoipCall, Voicemail } from './api/appels.api'
import { tasksAPI } from '../mes-taches/api/tasks.api'
import type { User as TaskUser } from '../mes-taches/types/task.types'
import './appels.css'

// Page "Appels" — historique des appels du bureau (entrants, sortants,
// manqués) + messagerie vocale partagée, avec attribution d'un message
// vocal à une affaire. Données servies par le backend (proxy du
// control-plane VoIP + méta locale voip_voicemail_meta).

type Section = 'historique' | 'voicemail'
type CallFilter = 'all' | 'inbound' | 'outbound' | 'missed'

function formatDateTime(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString('fr-BE', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/** Le caller-id Asterisk arrive brut (`"+32479…" <0032479…>`) — on
 *  n'affiche que la forme lisible, en retirant l'éventuel préfixe
 *  "Affaire N - " posé par le flux IVR. */
function displayCallerNumber(caller: string | null | undefined): string {
  const raw = String(caller ?? '').trim()
  const m = raw.match(/"([^"]*)"\s*<([^>]*)>/)
  if (m) {
    const disp = m[1].replace(/^Affaire\s+\d+\s*-\s*/i, '').trim()
    return disp || m[2]
  }
  return raw
}

function formatDuration(totalSec: number | null | undefined): string {
  if (totalSec == null || !Number.isFinite(totalSec) || totalSec <= 0) return '—'
  const m = Math.floor(totalSec / 60)
  const s = Math.round(totalSec % 60)
  return `${m}:${String(s).padStart(2, '0')}`
}

// Lecteur audio à chargement différé : le blob n'est téléchargé (avec
// auth) qu'au premier clic, puis lu dans un <audio> inline.
const LazyAudio: React.FC<{
  fetchBlob: () => Promise<Blob>
  onFirstPlay?: () => void
}> = ({ fetchBlob, onFirstPlay }) => {
  const [url, setUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const firedRef = useRef(false)

  useEffect(() => {
    return () => {
      if (url) URL.revokeObjectURL(url)
    }
  }, [url])

  const load = async () => {
    setLoading(true)
    setError(false)
    try {
      const blob = await fetchBlob()
      setUrl(URL.createObjectURL(blob))
      if (!firedRef.current) {
        firedRef.current = true
        onFirstPlay?.()
      }
    } catch {
      setError(true)
    } finally {
      setLoading(false)
    }
  }

  // L'emplacement a une taille FIXE : le bouton et le lecteur s'y
  // succèdent sans changer la largeur de colonne ni la hauteur de
  // ligne du tableau.
  return (
    <span className="apl-audio-slot">
      {url ? (
        <audio className="apl-audio" controls autoPlay src={url} />
      ) : (
        <button
          type="button"
          className="apl-play-btn"
          onClick={load}
          disabled={loading}
          title="Écouter"
        >
          {loading ? '…' : error ? 'Réessayer ▶' : '▶ Écouter'}
        </button>
      )}
    </span>
  )
}

// Modale « Envoyer en tâche » : assigne le message vocal à un membre de
// l'équipe via le système mes-tâches. La transcription + le résumé sont
// joints automatiquement côté backend ; le n° d'affaire (facultatif) se
// pose au moment de la VALIDATION de la tâche, pas ici.
const SendTaskModal: React.FC<{
  vm: Voicemail
  onClose: () => void
  onSent: (r: {
    taskId: string
    taskAssignedTo: string
    taskCreatedAt: string
  }) => void
}> = ({ vm, onClose, onSent }) => {
  const [users, setUsers] = useState<TaskUser[]>([])
  const [userId, setUserId] = useState('')
  const [comment, setComment] = useState('')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    tasksAPI
      .getAllUsers()
      .then((r) => setUsers(r.data ?? []))
      .catch(() => setError('Impossible de charger les utilisateurs'))
  }, [])

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!userId || !vm.when) return
    setSending(true)
    setError(null)
    try {
      const r = await appelsAPI.sendAsTask({
        caller: vm.caller,
        when: vm.when,
        assignedToUserId: userId,
        comment: comment.trim() || undefined,
      })
      onSent(r)
    } catch {
      setError("L'envoi a échoué — réessayez.")
      setSending(false)
    }
  }

  return (
    <div className="apl-modal-overlay" onClick={onClose}>
      <div className="apl-modal" onClick={(e) => e.stopPropagation()}>
        <div className="apl-modal-header">
          <h2>Envoyer en tâche</h2>
          <button
            type="button"
            className="apl-modal-close"
            onClick={onClose}
            aria-label="Fermer"
          >
            ✕
          </button>
        </div>
        <p className="apl-modal-context">
          Message vocal de{' '}
          <strong>
            {vm.contactName || displayCallerNumber(vm.caller) || 'Numéro inconnu'}
          </strong>{' '}
          du {formatDateTime(vm.when)}
        </p>
        {error && <div className="apl-error">{error}</div>}
        <form onSubmit={submit} className="apl-modal-form">
          <label className="apl-modal-label" htmlFor="apl-task-user">
            Assigner à
          </label>
          <select
            id="apl-task-user"
            className="apl-modal-select"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            required
          >
            <option value="" disabled>
              Choisir un membre de l'équipe…
            </option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.first_name} {u.last_name} ({u.role})
              </option>
            ))}
          </select>
          <label className="apl-modal-label" htmlFor="apl-task-comment">
            Commentaire (facultatif)
          </label>
          <textarea
            id="apl-task-comment"
            className="apl-modal-textarea"
            rows={3}
            placeholder="Instructions pour le destinataire…"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
          <p className="apl-modal-hint">
            La transcription et le résumé du message sont joints
            automatiquement à la tâche. Le n° d'affaire pourra être lié au
            moment de la validation de la tâche.
          </p>
          <div className="apl-modal-actions">
            <button
              type="button"
              className="apl-vm-ghost-btn"
              onClick={onClose}
              disabled={sending}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="apl-vm-link-btn"
              disabled={sending || !userId}
            >
              {sending ? 'Envoi…' : 'Envoyer la tâche'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

const StatusChip: React.FC<{ call: VoipCall }> = ({ call }) => {
  if (call.voicemail) return <span className="apl-chip apl-chip-vm">Messagerie</span>
  if (call.endCause === 'ANSWERED')
    return <span className="apl-chip apl-chip-ok">Répondu</span>
  if (call.endCause === 'BUSY')
    return <span className="apl-chip apl-chip-missed">Occupé</span>
  if (call.endCause === 'FAILED')
    return <span className="apl-chip apl-chip-missed">Échec</span>
  return <span className="apl-chip apl-chip-missed">Manqué</span>
}

export const AppelsTab: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const basePath = location.pathname.startsWith('/bureau') ? '/bureau' : '/admin'

  const [section, setSection] = useState<Section>('historique')
  const [calls, setCalls] = useState<VoipCall[]>([])
  const [voicemails, setVoicemails] = useState<Voicemail[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState<CallFilter>('all')
  const [vmView, setVmView] = useState<'inbox' | 'archived'>('inbox')
  // Saisie du n° d'affaire par message vocal (clé = id du message)
  const [affaireInputs, setAffaireInputs] = useState<Record<string, string>>({})
  const [savingId, setSavingId] = useState<string | null>(null)
  // Message vocal en cours d'« envoi en tâche » (modale ouverte)
  const [taskVm, setTaskVm] = useState<Voicemail | null>(null)
  // Attribution d'affaire depuis le journal d'appels (clé = callId)
  const [callAffaireInputs, setCallAffaireInputs] = useState<
    Record<string, string>
  >({})
  const [editingCallId, setEditingCallId] = useState<string | null>(null)
  const [savingCallId, setSavingCallId] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [c, v] = await Promise.all([
        appelsAPI.listCalls(),
        appelsAPI.listVoicemails(),
      ])
      setCalls(c)
      setVoicemails(v)
    } catch {
      setError('Impossible de charger les appels — réessayez.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    refresh()
  }, [refresh])

  // Tant que des transcriptions sont en cours côté serveur, on
  // rafraîchit silencieusement la liste (sans spinner) pour les voir
  // apparaître au fil de l'eau.
  useEffect(() => {
    if (!voicemails.some((v) => v.transcriptionStatus === 'pending')) return
    const t = setTimeout(() => {
      appelsAPI
        .listVoicemails()
        .then(setVoicemails)
        .catch(() => undefined)
    }, 15000)
    return () => clearTimeout(t)
  }, [voicemails])

  const filteredCalls = useMemo(() => {
    switch (filter) {
      case 'inbound':
        return calls.filter((c) => c.direction === 'inbound')
      case 'outbound':
        return calls.filter((c) => c.direction === 'outbound')
      case 'missed':
        return calls.filter((c) => c.missed || c.voicemail)
      default:
        return calls
    }
  }, [calls, filter])

  const unreadCount = useMemo(
    () => voicemails.filter((v) => !v.readAt && !v.archivedAt).length,
    [voicemails],
  )

  const visibleVoicemails = useMemo(
    () =>
      voicemails.filter((v) =>
        vmView === 'archived' ? !!v.archivedAt : !v.archivedAt,
      ),
    [voicemails, vmView],
  )
  const archivedCount = useMemo(
    () => voicemails.filter((v) => !!v.archivedAt).length,
    [voicemails],
  )

  const openAffaire = (affaireId: number) => {
    navigate(`${basePath}/tracker/${affaireId}`)
  }

  const markRead = async (vm: Voicemail, read: boolean) => {
    if (!vm.when) return
    try {
      await appelsAPI.setRead({ caller: vm.caller, when: vm.when, read })
      setVoicemails((prev) =>
        prev.map((v) =>
          v.id === vm.id
            ? { ...v, readAt: read ? new Date().toISOString() : null }
            : v,
        ),
      )
      // Rafraîchit le badge navbar sans attendre le prochain polling.
      void queryClient.invalidateQueries({
        queryKey: ['voip', 'voicemail', 'unread-count'],
      })
    } catch {
      /* non bloquant */
    }
  }

  const setArchived = async (vm: Voicemail, archived: boolean) => {
    if (!vm.when) return
    setSavingId(vm.id)
    try {
      await appelsAPI.setArchived({ caller: vm.caller, when: vm.when, archived })
      setVoicemails((prev) =>
        prev.map((v) =>
          v.id === vm.id
            ? { ...v, archivedAt: archived ? new Date().toISOString() : null }
            : v,
        ),
      )
      void queryClient.invalidateQueries({
        queryKey: ['voip', 'voicemail', 'unread-count'],
      })
    } catch {
      setError("L'archivage a échoué — réessayez.")
    } finally {
      setSavingId(null)
    }
  }

  // Lier / re-lier / délier un appel du journal à une affaire. Le lien
  // manuel prime sur l'affaire IVR côté backend (listCalls).
  const saveCallAffaire = async (call: VoipCall, affaireId: number | null) => {
    setSavingCallId(call.callId)
    try {
      await appelsAPI.setCallAffaire(call.callId, affaireId, {
        callerNumber: call.externalNumber || undefined,
        direction: call.direction,
        startedAt: call.startedAt || undefined,
        durationMs: call.durationMs || undefined,
      })
      setCalls((prev) =>
        prev.map((c) => (c.callId === call.callId ? { ...c, affaireId } : c)),
      )
      setEditingCallId(null)
      setCallAffaireInputs((prev) => ({ ...prev, [call.callId]: '' }))
    } catch {
      setError("L'attribution a échoué — réessayez.")
    } finally {
      setSavingCallId(null)
    }
  }

  const saveAttribution = async (vm: Voicemail, affaireId: number | null) => {
    if (!vm.when) return
    setSavingId(vm.id)
    try {
      await appelsAPI.setAttribution({
        caller: vm.caller,
        when: vm.when,
        affaireId,
        voicemailId: vm.id,
      })
      setVoicemails((prev) =>
        prev.map((v) => (v.id === vm.id ? { ...v, affaireId } : v)),
      )
      setAffaireInputs((prev) => ({ ...prev, [vm.id]: '' }))
    } catch {
      setError("L'attribution a échoué — réessayez.")
    } finally {
      setSavingId(null)
    }
  }

  return (
    <div className="apl-page">
      <div className="apl-header">
        <div>
          <h1 className="apl-title">Appels</h1>
          <p className="apl-subtitle">
            Historique des appels du bureau et messagerie vocale
          </p>
        </div>
        <button
          type="button"
          className="apl-refresh"
          onClick={refresh}
          disabled={loading}
        >
          {loading ? 'Chargement…' : 'Actualiser'}
        </button>
      </div>

      <div className="apl-sections">
        <button
          type="button"
          className={`apl-section-btn ${section === 'historique' ? 'active' : ''}`}
          onClick={() => setSection('historique')}
        >
          Historique
        </button>
        <button
          type="button"
          className={`apl-section-btn ${section === 'voicemail' ? 'active' : ''}`}
          onClick={() => setSection('voicemail')}
        >
          Messagerie vocale
          {unreadCount > 0 && <span className="apl-badge">{unreadCount}</span>}
        </button>
      </div>

      {error && <div className="apl-error">{error}</div>}

      {section === 'historique' && (
        <>
          <div className="apl-filters">
            {(
              [
                ['all', 'Tous'],
                ['inbound', 'Entrants'],
                ['outbound', 'Sortants'],
                ['missed', 'Manqués'],
              ] as [CallFilter, string][]
            ).map(([key, label]) => (
              <button
                key={key}
                type="button"
                className={`apl-filter-chip ${filter === key ? 'active' : ''}`}
                onClick={() => setFilter(key)}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="apl-table-wrap">
            <table className="apl-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th className="apl-center">Sens</th>
                  <th>Numéro</th>
                  <th>Agent</th>
                  <th className="apl-center">Durée</th>
                  <th>Statut</th>
                  <th>Dossier</th>
                  <th>Enregistrement</th>
                </tr>
              </thead>
              <tbody>
                {filteredCalls.length === 0 && !loading && (
                  <tr>
                    <td colSpan={8} className="apl-empty">
                      Aucun appel
                    </td>
                  </tr>
                )}
                {filteredCalls.map((call) => (
                  <tr key={call.id} className={call.missed ? 'apl-row-missed' : ''}>
                    <td className="apl-nowrap">{formatDateTime(call.startedAt)}</td>
                    <td className="apl-center">
                      <span
                        className={`apl-dir apl-dir-${call.direction}`}
                        title={call.direction === 'inbound' ? 'Entrant' : 'Sortant'}
                      >
                        {call.direction === 'inbound' ? '↓' : '↑'}
                      </span>
                    </td>
                    <td>
                      <div className="apl-number">{call.externalNumber || '—'}</div>
                      {call.contactName && (
                        <div className="apl-contact">{call.contactName}</div>
                      )}
                    </td>
                    <td>{call.agentName || call.agentEmail?.split('@')[0] || '—'}</td>
                    <td className="apl-nowrap apl-center">
                      {formatDuration(Math.round((call.durationMs || 0) / 1000))}
                    </td>
                    <td>
                      <StatusChip call={call} />
                    </td>
                    <td className="apl-call-attr-cell">
                      {call.affaireId && editingCallId !== call.callId ? (
                        <div className="apl-vm-linked">
                          <button
                            type="button"
                            className="apl-affaire-chip"
                            onClick={() => openAffaire(call.affaireId!)}
                            title="Ouvrir le tracker"
                          >
                            {call.affaireId} ↗
                          </button>
                          <button
                            type="button"
                            className="apl-unlink"
                            disabled={savingCallId === call.callId}
                            aria-label="Modifier le dossier lié"
                            title="Modifier ou retirer le dossier lié"
                            onClick={() => {
                              setEditingCallId(call.callId)
                              setCallAffaireInputs((prev) => ({
                                ...prev,
                                [call.callId]: String(call.affaireId),
                              }))
                            }}
                          >
                            ✎
                          </button>
                        </div>
                      ) : (
                        <form
                          className="apl-vm-attr"
                          onSubmit={(e) => {
                            e.preventDefault()
                            const raw = (
                              callAffaireInputs[call.callId] || ''
                            ).trim()
                            if (raw === '') {
                              // Champ vidé en mode édition = retirer le lien
                              if (call.affaireId) saveCallAffaire(call, null)
                              return
                            }
                            const n = parseInt(raw, 10)
                            if (Number.isFinite(n) && n > 0)
                              saveCallAffaire(call, n)
                          }}
                        >
                          <input
                            className="apl-vm-input"
                            type="number"
                            min={1}
                            placeholder="N° affaire"
                            aria-label="Numéro d'affaire à lier à cet appel"
                            title={
                              call.affaireId
                                ? 'Laisser vide et valider pour retirer le lien'
                                : undefined
                            }
                            value={callAffaireInputs[call.callId] || ''}
                            onChange={(e) =>
                              setCallAffaireInputs((prev) => ({
                                ...prev,
                                [call.callId]: e.target.value,
                              }))
                            }
                          />
                          <button
                            type="submit"
                            className="apl-vm-link-btn"
                            disabled={
                              savingCallId === call.callId ||
                              (!callAffaireInputs[call.callId] &&
                                !call.affaireId)
                            }
                          >
                            {savingCallId === call.callId
                              ? '…'
                              : callAffaireInputs[call.callId]
                                ? 'Lier'
                                : call.affaireId
                                  ? 'Retirer'
                                  : 'Lier'}
                          </button>
                          {editingCallId === call.callId && (
                            <button
                              type="button"
                              className="apl-unlink"
                              aria-label="Annuler la modification"
                              title="Annuler"
                              onClick={() => setEditingCallId(null)}
                            >
                              ✕
                            </button>
                          )}
                        </form>
                      )}
                    </td>
                    <td>
                      {call.hasRecording ? (
                        <LazyAudio
                          fetchBlob={() => appelsAPI.callRecordingBlob(call.id)}
                        />
                      ) : (
                        '—'
                      )}
                      {call.summary && (
                        <div className="apl-summary" title={call.summary}>
                          {call.summary}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {section === 'voicemail' && (
        <div className="apl-vm-list">
          <div className="apl-filters">
            <button
              type="button"
              className={`apl-filter-chip ${vmView === 'inbox' ? 'active' : ''}`}
              onClick={() => setVmView('inbox')}
            >
              Boîte de réception
            </button>
            <button
              type="button"
              className={`apl-filter-chip ${vmView === 'archived' ? 'active' : ''}`}
              onClick={() => setVmView('archived')}
            >
              Archivés{vmView === 'inbox' && archivedCount > 0 ? ` (${archivedCount})` : ''}
            </button>
          </div>
          {visibleVoicemails.length === 0 && !loading ? (
            <div className="apl-empty">
              {vmView === 'archived' ? 'Aucun message archivé' : 'Aucun message vocal'}
            </div>
          ) : (
            <div className="apl-table-wrap">
              <table className="apl-table apl-vm-table">
                <thead>
                  <tr>
                    <th>Appelant</th>
                    <th>Date</th>
                    <th className="apl-center">Durée</th>
                    <th className="apl-vm-th-message">Message</th>
                    <th>Écoute</th>
                    <th>Dossier</th>
                    <th>
                      <span className="apl-sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {visibleVoicemails.map((vm) => {
                    const isNew = !vm.readAt && !vm.archivedAt
                    return (
                      <tr
                        key={vm.id}
                        className={
                          isNew
                            ? 'apl-vm-row-unread'
                            : vm.archivedAt
                              ? 'apl-vm-row-archived'
                              : ''
                        }
                      >
                        <td>
                          <div className="apl-vm-caller">
                            {isNew && (
                              <span className="apl-vm-dot" aria-hidden="true" />
                            )}
                            <span className="apl-vm-identity">
                              {vm.contactName ||
                                displayCallerNumber(vm.caller) ||
                                'Numéro inconnu'}
                            </span>
                          </div>
                          {vm.contactName && (
                            <div className="apl-vm-number">
                              {displayCallerNumber(vm.caller)}
                            </div>
                          )}
                          {isNew && (
                            <span className="apl-vm-new-badge">Nouveau</span>
                          )}
                        </td>
                        <td className="apl-nowrap">{formatDateTime(vm.when)}</td>
                        <td className="apl-nowrap apl-center">
                          {formatDuration(vm.durationSec)}
                        </td>
                        <td className="apl-vm-message-cell">
                          {vm.transcriptionStatus === 'pending' && (
                            <span className="apl-vm-transcribing" role="status">
                              <span
                                className="apl-vm-transcribing-pulse"
                                aria-hidden="true"
                              />
                              Transcription en cours…
                            </span>
                          )}
                          {vm.summary && (
                            <p className="apl-vm-summary">{vm.summary}</p>
                          )}
                          {vm.transcription && (
                            <details className="apl-vm-transcript">
                              <summary>Transcription complète</summary>
                              <p>{vm.transcription}</p>
                            </details>
                          )}
                          {vm.readAt && vm.readBy && (
                            <div className="apl-vm-readby">
                              écouté par {vm.readBy}
                            </div>
                          )}
                        </td>
                        <td className="apl-vm-audio-cell">
                          <LazyAudio
                            fetchBlob={() => appelsAPI.voicemailAudioBlob(vm.id)}
                            onFirstPlay={() => {
                              if (!vm.readAt) markRead(vm, true)
                            }}
                          />
                        </td>
                        <td className="apl-vm-attr-cell">
                          {vm.affaireId ? (
                            <div className="apl-vm-linked">
                              <button
                                type="button"
                                className="apl-affaire-chip"
                                onClick={() => openAffaire(vm.affaireId!)}
                                title="Ouvrir le tracker"
                              >
                                {vm.affaireId} ↗
                              </button>
                              <button
                                type="button"
                                className="apl-unlink"
                                onClick={() => saveAttribution(vm, null)}
                                disabled={savingId === vm.id}
                                aria-label="Retirer l'attribution"
                                title="Retirer l'attribution"
                              >
                                ✕
                              </button>
                            </div>
                          ) : (
                            <>
                              {vm.suggestedAffaires.length > 0 && (
                                <div className="apl-vm-suggest">
                                  {vm.suggestedAffaires.map((s) => (
                                    <button
                                      key={String(s.id)}
                                      type="button"
                                      className="apl-vm-suggest-chip"
                                      disabled={savingId === vm.id}
                                      title={`Attribuer à ce dossier${s.address ? ` — ${s.address}` : ''}${s.status ? ` (${s.status})` : ''}`}
                                      onClick={() =>
                                        saveAttribution(vm, Number(s.id))
                                      }
                                    >
                                      #{s.id}
                                    </button>
                                  ))}
                                </div>
                              )}
                              <form
                                className="apl-vm-attr"
                                onSubmit={(e) => {
                                  e.preventDefault()
                                  const n = parseInt(affaireInputs[vm.id] || '', 10)
                                  if (Number.isFinite(n) && n > 0)
                                    saveAttribution(vm, n)
                                }}
                              >
                                <input
                                  className="apl-vm-input"
                                  type="number"
                                  min={1}
                                  placeholder="N° affaire"
                                  aria-label="Numéro d'affaire à attribuer"
                                  value={affaireInputs[vm.id] || ''}
                                  onChange={(e) =>
                                    setAffaireInputs((prev) => ({
                                      ...prev,
                                      [vm.id]: e.target.value,
                                    }))
                                  }
                                />
                                <button
                                  type="submit"
                                  className="apl-vm-link-btn"
                                  disabled={
                                    savingId === vm.id || !affaireInputs[vm.id]
                                  }
                                >
                                  {savingId === vm.id ? '…' : 'Attribuer'}
                                </button>
                              </form>
                            </>
                          )}
                        </td>
                        <td className="apl-vm-actions-cell">
                          {vm.taskId && vm.taskDone === false ? (
                            <span
                              className="apl-vm-task-chip"
                              title={`Tâche en cours chez ${vm.taskAssignedTo ?? '?'} — envoyée le ${formatDateTime(vm.taskCreatedAt)}`}
                            >
                              ⏳ Tâche → {vm.taskAssignedTo ?? '—'}
                            </span>
                          ) : (
                            <>
                              {vm.taskId && vm.taskDone && (
                                <span
                                  className="apl-vm-task-chip apl-vm-task-done"
                                  title={`Tâche validée — ${vm.taskAssignedTo ?? ''}`}
                                >
                                  ✓ Tâche validée
                                </span>
                              )}
                              <button
                                type="button"
                                className="apl-vm-ghost-btn"
                                title="Envoyer ce message comme tâche à un membre de l'équipe"
                                onClick={() => setTaskVm(vm)}
                              >
                                ➤ Envoyer en tâche
                              </button>
                            </>
                          )}
                          <button
                            type="button"
                            className="apl-vm-ghost-btn"
                            onClick={() => markRead(vm, !vm.readAt)}
                          >
                            {vm.readAt ? 'Non écouté' : 'Écouté'}
                          </button>
                          <button
                            type="button"
                            className="apl-vm-ghost-btn"
                            disabled={savingId === vm.id}
                            title={
                              vm.archivedAt
                                ? 'Remettre dans la boîte de réception'
                                : "Archiver — retiré de la boîte et du badge, l'audio reste consultable"
                            }
                            onClick={() => setArchived(vm, !vm.archivedAt)}
                          >
                            {vm.archivedAt ? '↩ Désarchiver' : '🗄 Archiver'}
                          </button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {taskVm && (
        <SendTaskModal
          vm={taskVm}
          onClose={() => setTaskVm(null)}
          onSent={(r) => {
            setVoicemails((prev) =>
              prev.map((v) =>
                v.id === taskVm.id
                  ? {
                      ...v,
                      taskId: r.taskId,
                      taskAssignedTo: r.taskAssignedTo,
                      taskCreatedAt: r.taskCreatedAt,
                      taskDone: false,
                    }
                  : v,
              ),
            )
            setTaskVm(null)
          }}
        />
      )}
    </div>
  )
}
